import {Modal, ModalBody, ModalContent, ModalHeader} from "@heroui/react";
import {addToast} from "@heroui/toast";
import {useEffect, useState} from "react";
import {apiActionImproved} from "../../api.ts";
import {UserRole} from "../../constants.ts";
import {useJwtAuth} from "../../JWTAuthProvider.tsx";
import PButton from "../PButton.tsx";
import PInput from "../PInput.tsx";

/**
 * A player who signs up with Google from an event page arrives with a bare account (the socialite
 * callback makes it EmptyAfterSignup) that no registration endpoint accepts (User::canEnterEvents).
 * This is the minimal profile, inline: confirm the name, and the account becomes a player's — the
 * same submit-questionnaire the full sign-up ends with, the sport taken from the event — and the
 * registration carries on where it was (onDone).
 */
export function needsPlayerProfile(user: any): boolean {
    return !!user && +user.role === UserRole.EmptyAfterSignup;
}

export default function FinishPlayerProfileModal({open, sport, onClose, onDone}: {
    open: boolean;
    sport?: string | null;
    onClose: () => void;
    onDone: () => void;
}) {
    const jwt = useJwtAuth();
    const user: any = jwt.user;
    const [first, setFirst] = useState('');
    const [last, setLast] = useState('');
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (open) {
            setFirst(user?.name || '');
            setLast(user?.lastname || '');
        }
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    const submit = async () => {
        if (!first.trim()) {
            addToast({color: 'warning', title: 'Add your first name to continue.'});
            return;
        }
        setBusy(true);
        try {
            if (first.trim() !== (user?.name || '') || last.trim() !== (user?.lastname || '')) {
                await apiActionImproved('users-update', {uid: user.uid, values: {version: (user.version || 0) + 1, name: first.trim(), lastname: last.trim()}});
            }
            await apiActionImproved('submit-questionnaire', {
                role: UserRole.Player,
                sport: sport || null,
                elo_grade: null, nationality: null, how_often: null, how_long_playing: null,
                is_last_step: true,
            });
            await jwt.reloadUser();
            onDone();
        } catch (e: any) {
            addToast({color: 'danger', title: e?.message || 'Could not finish your profile. Please try again.'});
        } finally {
            setBusy(false);
        }
    };

    return (
        <Modal isOpen={open} placement="center" onOpenChange={(o) => { if (!o && !busy) onClose(); }}>
            <ModalContent>
                <ModalHeader>One last step</ModalHeader>
                <ModalBody className="pb-6">
                    <p className="text-sm text-default-600">You signed in with Google. Confirm your name and you can enter straight away.</p>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <PInput label="First name" isRequired value={first} onValueChange={setFirst} autoFocus />
                        <PInput label="Last name" value={last} onValueChange={setLast} />
                    </div>
                    <PButton color="primary" isLoading={busy} onPress={submit} data-testid="finish-player-profile">Continue to register</PButton>
                </ModalBody>
            </ModalContent>
        </Modal>
    );
}
